import { api } from './api';

function auth(token: string) {
  return { accessToken: token };
}

/**
 * Compagnies interrogées par opportunité (demande client LBC 2026-05-22).
 *
 * Endpoints :
 *   GET    /api/v1/opportunities/:oppId/inquiries
 *   POST   /api/v1/opportunities/:oppId/inquiries  body { insuranceCompanyIds[], notes? }
 *   POST   /api/v1/inquiries/:id/refuse            body { reason? }
 *   POST   /api/v1/inquiries/:id/restore-pending
 *   DELETE /api/v1/inquiries/:id
 *
 * RBAC : writers (admin/director/manager/collaborator) + scope filter.
 */

export type InquiryStatus = 'pending' | 'quote_received' | 'refused';

export interface Inquiry {
  id: string;
  opportunityId: string;
  insuranceCompanyId: string;
  status: InquiryStatus;
  refusedReason: string | null;
  notes: string | null;
  requestedAt: string;
  quoteReceivedAt: string | null;
  refusedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  insuranceCompany: { id: string; name: string };
  creator: { id: string; firstName: string; lastName: string };
}

export interface CreateInquiriesBody {
  insuranceCompanyIds: string[];
  notes?: string;
}

export const inquiriesApi = {
  list: (token: string, oppId: string) =>
    api.get<Inquiry[]>(`/api/v1/opportunities/${oppId}/inquiries`, auth(token)),

  /** Création en bulk : 1 à 20 compagnies, status pending pour chacune. */
  create: (token: string, oppId: string, body: CreateInquiriesBody) =>
    api.post<Inquiry[]>(`/api/v1/opportunities/${oppId}/inquiries`, body, auth(token)),

  /** Passe en `refused`. `reason` optionnelle (texte libre). */
  refuse: (token: string, inquiryId: string, reason?: string) =>
    api.post<Inquiry>(`/api/v1/inquiries/${inquiryId}/refuse`, { reason }, auth(token)),

  /** Restaure en `pending`. Refuse en 422 si l'inquiry est en `quote_received`. */
  restorePending: (token: string, inquiryId: string) =>
    api.post<Inquiry>(`/api/v1/inquiries/${inquiryId}/restore-pending`, {}, auth(token)),

  /** Supprime l'inquiry. Refuse en 422 si un Quote existe pour la (opp, company). */
  delete: (token: string, inquiryId: string) =>
    api.delete<void>(`/api/v1/inquiries/${inquiryId}`, auth(token)),
};

/**
 * Source de vérité des libellés FR + couleurs badge pour le statut inquiry.
 * Pattern miroir `STAGE_VISUAL` / `ORIGIN_VISUAL`.
 */
export const INQUIRY_STATUS_VISUAL: Record<
  InquiryStatus,
  { label: string; badge: string }
> = {
  pending: { label: 'En attente', badge: 'bg-warning-50 text-warning-600' },
  quote_received: { label: 'Devis reçu', badge: 'bg-success-50 text-success-600' },
  refused: { label: 'Refusé', badge: 'bg-danger-50 text-danger-600' },
};
